import React, { ReactNode } from "react";
import { Box, Typography, SxProps, Theme } from "@mui/material";
import { OverflowTooltip } from "../OverflowTooltip";
import { COLOR_CONSTANTS, TYPOGRAPHY_SCALE } from "../../constants/styles";
import { SPACING_CONSTANTS } from "../../constants/styles/spacing";

interface ValueTextProps {
  /**
   * 메인 값 텍스트
   */
  value?: string | ReactNode;
  /**
   * 단위 텍스트
   */
  unit?: string;
  /**
   * 커스텀 스타일
   */
  sx?: SxProps<Theme>;
  /**
   * 값 텍스트 색상 (기본값: primary[600])
   */
  valueColor?: string;
  /**
   * 전체 너비 사용 여부 (Right 영역을 넓게 사용)
   */
  widthFull?: boolean;
  /**
   * 텍스트 두께
   */
  fontWeight?: number;
  /**
   * 텍스트 정렬
   */
  textAlign?: "left" | "center" | "right";
  /**
   * 잘렸을 때 hover 팝업으로 보여줄 본문.
   * `value` 가 문자열이면 생략해도 그 값이 쓰인다. `value` 가 노드일 때만 필요하다.
   */
  tooltipText?: string;
}

/**
 * DataCell의 오른쪽 영역에 사용할 값/단위 텍스트 컴포넌트
 *
 * 값이 칸을 넘치면 hover 팝업으로 전체 내용을 보여준다 — 잘렸을 때만 열린다.
 * 팝업 본문은 문자열 `value` 자신, 또는 호출부가 준 `tooltipText` 다. `value` 가 노드인데
 * `tooltipText` 가 없으면 본문이 없으므로 말줄임만 하고 팝업은 붙이지 않는다.
 */
export const ValueText: React.FC<ValueTextProps> = ({
  value,
  unit,
  sx,
  valueColor = COLOR_CONSTANTS.text.primary[600],
  widthFull = false,
  fontWeight,
  textAlign,
  tooltipText,
}) => {
  // 공통 텍스트 스타일
  const textStyles = {
    ...TYPOGRAPHY_SCALE.subtitle2,
    color: valueColor,
    fontWeight: fontWeight || TYPOGRAPHY_SCALE.subtitle2.fontWeight,
    textAlign: textAlign || "right",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  // 팝업 본문은 문자열 값 자신이거나 호출부가 준 `tooltipText` 다.
  // 노드 값을 본문으로 복제하면 노드가 이미 품은 팝업과 중첩되므로 복제하지 않는다.
  const popupText =
    tooltipText ?? (typeof value === "string" ? value : undefined);

  // 본문이 없으면 팝업을 열 근거가 없다 — 말줄임만 하고 팝업은 붙이지 않는다.
  const renderValue = (valueSx: SxProps<Theme>) =>
    popupText === undefined ? (
      <Typography component="div" noWrap sx={valueSx}>
        {value}
      </Typography>
    ) : (
      <OverflowTooltip
        component="div"
        text={value}
        popupText={popupText}
        sx={valueSx}
      />
    );

  // 단위가 있을 때: flex 레이아웃 사용
  if (unit) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: `${SPACING_CONSTANTS.spacing[1]}px`,
          width: widthFull ? "auto" : "100%",
          ...sx,
        }}
      >
        {value && renderValue({ ...textStyles })}
        <Typography
          sx={{
            ...TYPOGRAPHY_SCALE.caption2,
            flexShrink: 0,
            color: COLOR_CONSTANTS.text.disabled,
          }}
        >
          {unit}
        </Typography>
      </Box>
    );
  }

  // 값만 있을 때: 단순 텍스트
  return renderValue({
    ...textStyles,
    minWidth: 160,
    ...sx,
  });
};

interface RightContentProps {
  /**
   * 메인 텍스트 (값)
   */
  mainText?: string | ReactNode;
  /**
   * 서브 텍스트 (단위)
   */
  subText?: string;
  /**
   * 커스텀 스타일
   */
  sx?: SxProps<Theme>;
  /**
   * 값 텍스트 색상
   */
  valueColor?: string;
  /**
   * 전체 너비 사용 여부
   */
  widthFull?: boolean;
  /**
   * 텍스트 두께
   */
  fontWeight?: number;
  /**
   * 텍스트 정렬
   */
  textAlign?: "left" | "center" | "right";
  /**
   * 잘렸을 때 hover 팝업으로 보여줄 본문. `mainText` 가 노드일 때만 필요하다.
   */
  tooltipText?: string;
}

/**
 * 기존 RightContent와의 호환성을 위한 래퍼 컴포넌트
 */
export const RightContent: React.FC<RightContentProps> = ({
  mainText,
  subText,
  sx,
  valueColor,
  widthFull,
  fontWeight,
  textAlign,
  tooltipText,
}) => (
  <ValueText
    value={mainText}
    unit={subText}
    sx={sx}
    valueColor={valueColor}
    widthFull={widthFull}
    fontWeight={fontWeight}
    textAlign={textAlign}
    tooltipText={tooltipText}
  />
);

export type { ValueTextProps, RightContentProps };
